/**
 * 面包屑导航 Hook
 * Requirements: 7.1, 7.4, 7.5
 */

import { useLocation } from '@tanstack/react-router'
import { useMemo } from 'react'

import {
  PATH_TITLES,
  getDynamicRouteTitle,
  generateBreadcrumbFromPath,
} from '@/config/breadcrumb-config'

import type { BreadcrumbItem } from '@/components/common/breadcrumb'

export interface BreadcrumbContext {
  /** 项目名称（用于项目详情页） */
  projectName?: string
  /** 任务名称（用于任务详情页） */
  issueName?: string
  /** 团队名称（用于团队详情页） */
  teamName?: string
  /** 员工名称（用于员工详情页） */
  employeeName?: string
}

/**
 * 使用面包屑导航
 * @param context 上下文信息，用于显示动态标题
 */
export function useBreadcrumb(context?: BreadcrumbContext): BreadcrumbItem[] {
  const location = useLocation()
  const pathname = location.pathname

  const items = useMemo(() => {
    const breadcrumbItems = generateBreadcrumbFromPath(pathname, context)
    
    // 转换为 BreadcrumbItem 格式
    return breadcrumbItems.map((item) => ({
      label: item.label,
      path: item.path,
    }))
  }, [pathname, context])

  return items
}

/**
 * 获取当前页面标题
 */
export function usePageTitle(): string {
  const location = useLocation()
  const pathname = location.pathname

  return useMemo(() => {
    // 先尝试静态标题
    const staticTitle = PATH_TITLES[pathname]
    if (staticTitle) {return staticTitle}

    // 尝试动态标题
    const segments = pathname.split('/').filter(Boolean)
    const lastSegment = segments[segments.length - 1]
    const dynamicTitle = getDynamicRouteTitle(lastSegment, pathname)
    if (dynamicTitle) {return dynamicTitle}

    return '页面'
  }, [pathname])
}

/**
 * 判断当前是否为详情页
 */
export function useIsDetailPage(): boolean {
  const location = useLocation()
  const pathname = location.pathname

  return useMemo(() => {
    const segments = pathname.split('/').filter(Boolean)
    const lastSegment = segments[segments.length - 1]
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(lastSegment)
  }, [pathname])
}
